/**
 * Attempt facts, assembled once and shared by every analytics view.
 *
 * `data/types.ts` models what the *old* single-student screens needed: a score
 * point carries a ratio and a date, and that is all. The cohort dashboard and
 * the reworked progress screen need three things it does not carry — which
 * sitting an attempt was (`attempt_no`, so a retake is not mistaken for
 * progress), how long the student actually took against how long they were
 * allowed, and how often they dropped off the network (`rejoin_count`,
 * `offline_ms`). All three live on the wire's `Attempt` and come back from
 * `GET /api/admin/attempts` untouched, so this module re-reads that endpoint
 * and joins it to the exam list rather than widening the view models.
 *
 * Like the aggregates in `data/client.ts`, this is composition the API does not
 * do yet: when `GET /api/admin/analytics` lands it should serve these rows and
 * this file should shrink to a type import.
 */
import type { AttemptStatus } from "@exam/shared";
import { fetchAttempts, fetchExams } from "../data/client";
import type { Catalog } from "../data/types";

/** One sitting, with everything the charts need to be honest about it. */
export interface AttemptFact {
  attempt_id: string;
  exam_id: string;
  exam_title: string;
  subject_name: string;
  student_id: string;
  student_name: string;
  status: AttemptStatus;
  /** 1 for a first sitting, 2 for the first retake, and so on. */
  attempt_no: number;
  started_at: number | null;
  submitted_at: number | null;
  score: number | null;
  max_score: number | null;
  /**
   * Score as a fraction of the paper, or null when the paper carries no marks
   * or nothing was graded. Deliberately nullable rather than 0 — an ungraded
   * attempt plotted at zero reads as a student who scored nothing.
   */
  ratio: number | null;
  /** Wall-clock the attempt was open, or null if it never started/finished. */
  taken_ms: number | null;
  /** The exam's duration plus this attempt's own extra time, in ms. */
  allowed_ms: number | null;
  /** How many times the laptop went silent and came back mid-attempt. */
  rejoin_count: number;
  /** Total time the laptop was unreachable while the attempt was running. */
  offline_ms: number;
  total_violations: number;
}

const MINUTE_MS = 60_000;

/** Graded means the paper has a final mark: everything else is still in flight. */
export function isGraded(fact: AttemptFact): boolean {
  return fact.status === "submitted" || fact.status === "auto_submitted";
}

/** Chronological key — submitted time when there is one, else the start. */
function timeOf(fact: AttemptFact): number {
  return fact.submitted_at ?? fact.started_at ?? 0;
}

export async function fetchAttemptFacts(
  catalog: Catalog,
): Promise<AttemptFact[]> {
  const [list, exams] = await Promise.all([
    fetchAttempts(),
    fetchExams(catalog),
  ]);
  const examById = new Map(exams.map((e) => [e.id, e]));

  return list.attempts
    .map((row): AttemptFact => {
      const exam = examById.get(row.attempt.exam_id);
      const max = row.attempt.max_score;
      const started = row.attempt.started_at;
      const submitted = row.attempt.submitted_at;
      return {
        attempt_id: row.attempt.id,
        exam_id: row.attempt.exam_id,
        exam_title: row.exam_title,
        subject_name: exam?.subject_name ?? "—",
        student_id: row.attempt.user_id,
        student_name: row.student_name,
        status: row.attempt.status,
        attempt_no: row.attempt.attempt_no,
        started_at: started,
        submitted_at: submitted,
        score: row.attempt.score,
        max_score: max,
        ratio:
          max !== null && max > 0 && row.attempt.score !== null
            ? Math.max(0, row.attempt.score / max)
            : null,
        taken_ms:
          started !== null && submitted !== null && submitted > started
            ? submitted - started
            : null,
        allowed_ms:
          exam === undefined
            ? null
            : (exam.duration_min + row.attempt.extra_time_min) * MINUTE_MS,
        rejoin_count: row.attempt.rejoin_count,
        offline_ms: row.attempt.offline_ms,
        total_violations: row.total_violations,
      };
    })
    .sort((a, b) => timeOf(a) - timeOf(b));
}

/* ------------------------------------------------------------------ */
/* Cohort                                                              */
/* ------------------------------------------------------------------ */

export interface CohortStudentRow {
  student_id: string;
  student_name: string;
  /** Every sitting on record, including ones still running. */
  attempts: number;
  /** Sittings with a mark — the only ones the score numbers are drawn from. */
  graded: number;
  /** Sittings after the first against a paper they had already sat. */
  retakes: number;
  /** Mean of the graded ratios, or null when nothing is graded yet. */
  mean_ratio: number | null;
  best_ratio: number | null;
  /** The most recent graded sitting, which is the one worth acting on. */
  latest_ratio: number | null;
  violations: number;
  /** Every graded ratio, oldest first — the dots the cohort chart plots. */
  points: Array<{
    attempt_id: string;
    exam_title: string;
    attempt_no: number;
    submitted_at: number | null;
    ratio: number;
  }>;
}

export interface CohortSummary {
  students: CohortStudentRow[];
  /** Students with at least one sitting on record. */
  student_count: number;
  attempt_count: number;
  graded_count: number;
  /**
   * Mean across every graded attempt, not the mean of the per-student means:
   * a student who sat five papers should weigh five papers' worth here, and
   * the number is labelled "per attempt" wherever it is shown.
   */
  mean_ratio: number | null;
}

export function summariseCohort(facts: AttemptFact[]): CohortSummary {
  const byStudent = new Map<string, CohortStudentRow>();

  for (const fact of facts) {
    const row = byStudent.get(fact.student_id) ?? {
      student_id: fact.student_id,
      student_name: fact.student_name,
      attempts: 0,
      graded: 0,
      retakes: 0,
      mean_ratio: null,
      best_ratio: null,
      latest_ratio: null,
      violations: 0,
      points: [],
    };
    row.attempts++;
    row.violations += fact.total_violations;
    if (fact.attempt_no > 1) row.retakes++;
    if (isGraded(fact)) {
      row.graded++;
      if (fact.ratio !== null) {
        row.points.push({
          attempt_id: fact.attempt_id,
          exam_title: fact.exam_title,
          attempt_no: fact.attempt_no,
          submitted_at: fact.submitted_at,
          ratio: fact.ratio,
        });
      }
    }
    byStudent.set(fact.student_id, row);
  }

  const students = [...byStudent.values()];
  for (const row of students) {
    if (row.points.length === 0) continue;
    const sum = row.points.reduce((acc, p) => acc + p.ratio, 0);
    row.mean_ratio = sum / row.points.length;
    row.best_ratio = row.points.reduce((max, p) => Math.max(max, p.ratio), 0);
    row.latest_ratio = row.points[row.points.length - 1]!.ratio;
  }

  // Weakest first: the roster is read to find who needs help, and a student
  // with no graded attempt yet is not "worst" — they sort to the bottom.
  students.sort((a, b) => {
    if (a.mean_ratio === null && b.mean_ratio === null) {
      return a.student_name.localeCompare(b.student_name);
    }
    if (a.mean_ratio === null) return 1;
    if (b.mean_ratio === null) return -1;
    return a.mean_ratio - b.mean_ratio;
  });

  const graded = facts.filter((f) => isGraded(f) && f.ratio !== null);
  const meanRatio =
    graded.length === 0
      ? null
      : graded.reduce((acc, f) => acc + (f.ratio ?? 0), 0) / graded.length;

  return {
    students,
    student_count: students.length,
    attempt_count: facts.length,
    graded_count: facts.filter(isGraded).length,
    mean_ratio: meanRatio,
  };
}

/* ------------------------------------------------------------------ */
/* One student, over time                                              */
/* ------------------------------------------------------------------ */

export interface ScorePointFact {
  attempt_id: string;
  exam_id: string;
  exam_title: string;
  attempt_no: number;
  submitted_at: number | null;
  score: number | null;
  max_score: number | null;
  /** 0..1 — only graded points reach a series, so this is never null. */
  ratio: number;
  /**
   * True when the *previous* point in this series was the same paper, i.e. the
   * step between them is a resit rather than new ground. The chart draws that
   * step differently, because a rise across a retake is not the same claim as a
   * rise across two different papers.
   */
  follows_same_exam: boolean;
}

export interface ScoreSeries {
  subject_name: string;
  points: ScorePointFact[];
}

/** One series per subject, oldest sitting first, retakes marked. */
export function scoreSeriesFor(facts: AttemptFact[]): ScoreSeries[] {
  const bySubject = new Map<string, ScorePointFact[]>();

  for (const fact of facts) {
    if (!isGraded(fact) || fact.ratio === null) continue;
    const points = bySubject.get(fact.subject_name) ?? [];
    const previous = points[points.length - 1];
    points.push({
      attempt_id: fact.attempt_id,
      exam_id: fact.exam_id,
      exam_title: fact.exam_title,
      attempt_no: fact.attempt_no,
      submitted_at: fact.submitted_at,
      score: fact.score,
      max_score: fact.max_score,
      ratio: fact.ratio,
      follows_same_exam: previous !== undefined && previous.exam_id === fact.exam_id,
    });
    bySubject.set(fact.subject_name, points);
  }

  return [...bySubject.entries()]
    .map(([subject_name, points]) => ({ subject_name, points }))
    .sort((a, b) => a.subject_name.localeCompare(b.subject_name));
}
